import { useState } from "react";
import { Link } from "react-router-dom";
import { specials } from "@/mocks/specials";
import { getMovieById } from "@/utils/movie";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

export default function SpecialPage() {
  const [activeId, setActiveId] = useState(specials[0].id);
  const active = specials.find((special) => special.id === activeId) ?? specials[0];

  useSeo({
    title: "影评专题 - 黄色大片",
    description:
      "黄色大片影评专题，聚焦年度科幻、悬疑与温情佳作的深度解读，由编辑团队与影评人共同策划，带你读懂电影背后的表达。",
    keywords: "影评专题,电影影评,深度解读,黄色大片,电影推荐",
  });

  return (
    <div>
      <div className="relative w-full overflow-hidden bg-background-50">
        <div className="absolute inset-0">
          <img
            src={active.cover}
            alt={active.title}
            title={`${active.title} - 黄色大片 影评专题`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/90 to-background-50/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-6 md:px-6 md:pb-12 md:pt-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "影评专题" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 font-label text-xs uppercase tracking-widest text-foreground-950">
              <i className="ri-quill-pen-fill text-sm" />
              Editorial Feature
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black text-foreground-50 md:text-4xl">影评专题</h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-200 md:text-base">
              由编辑团队策划的深度专题，围绕年度热门影片展开多角度解读，帮你在观影前后都能有更多收获。
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
        <div className="no-scrollbar flex gap-2 overflow-x-auto border-b border-background-200/50 pb-4">
          {specials.map((special) => (
            <button
              key={special.id}
              type="button"
              onClick={() => setActiveId(special.id)}
              className={`flex-shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                special.id === activeId
                  ? "bg-primary-500 text-foreground-950"
                  : "bg-background-100 text-foreground-300 hover:bg-background-200/70 hover:text-foreground-50"
              }`}
            >
              {special.title.split("：")[0]}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-50">{active.title}</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-300 md:text-base">{active.intro}</p>

            <div className="mt-6 flex gap-3 rounded-lg border border-accent-500/30 bg-accent-100/40 p-5">
              <i className="ri-lightbulb-flash-line text-xl text-accent-400" />
              <p className="text-sm leading-relaxed text-foreground-200">{active.editorNote}</p>
            </div>

            <div className="mt-8 flex flex-col gap-4">
              {active.articles.map((article) => {
                const movie = getMovieById(article.movieId);
                return (
                  <article
                    key={article.id}
                    className="group rounded-lg border border-background-200/60 bg-background-100 p-5 transition-colors hover:border-primary-400/60"
                  >
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1 text-primary-400">
                        <i className="ri-user-3-line text-sm" />
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line text-sm" />
                        {article.date}
                      </span>
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-50">
                      <Link to={`/detail/${article.movieId}`} className="transition-colors hover:text-primary-400">
                        {article.title}
                      </Link>
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-400">{article.excerpt}</p>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200/50 pt-4">
                      {movie && (
                        <span className="flex items-center gap-2 text-xs text-foreground-500">
                          <i className="ri-film-line text-sm text-primary-400" />
                          关联影片：{movie.title}
                        </span>
                      )}
                      <Link
                        to={`/detail/${article.movieId}`}
                        className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-300 transition-colors hover:text-primary-400"
                      >
                        阅读全文
                        <i className="ri-arrow-right-line text-sm" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-50">相关专题</h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {specials.map((special) => (
                    <li key={special.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(special.id)}
                        className={`w-full text-left text-sm leading-relaxed transition-colors ${
                          special.id === activeId
                            ? "text-primary-400"
                            : "text-foreground-300 hover:text-primary-400"
                        }`}
                      >
                        <span className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line mt-0.5 text-base" />
                          {special.title}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200/60 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-50">本期影片速览</h2>
                <ul className="mt-4 flex flex-col gap-3">
                  {active.articles.map((article) => {
                    const movie = getMovieById(article.movieId);
                    if (!movie) return null;
                    return (
                      <li key={article.id}>
                        <Link
                          to={`/detail/${movie.id}`}
                          className="group flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-200/50"
                        >
                          <div className="h-14 w-10 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
                            <img
                              src={movie.poster}
                              alt={`${movie.title} 海报`}
                              className="h-full w-full object-cover object-top"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm text-foreground-100 group-hover:text-primary-400">
                              {movie.title}
                            </p>
                            <p className="text-xs text-primary-400">{movie.rating.toFixed(1)} 分</p>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}